import { FormEvent, useEffect, useRef, useState } from "react";
import NewWindow from "react-new-window";
import { loadFonts } from "src/components/CardGenerator";
import CgoCardSide from "src/components/CgoCard";
import { DeckInfo, FontStyleOverride, ProjectFontDefinition } from "src/components/types";

interface CardsContainerProps {
  deckInfo: DeckInfo;
  onlyOneCard: boolean;
  mmWidth: number;
  isRev: boolean;
  cardFilters?: Array<CardFilter>;
  globalAssets: { [key: string]: any };
  projectFonts: Array<ProjectFontDefinition>;
  deckFontOverrides?: Array<FontStyleOverride>;
  cardFontOverrides?: Array<FontStyleOverride>;
}

function CardsContainer(props: CardsContainerProps) {
  useEffect(() => {
    loadFonts(window, props.deckInfo);
  }, [props.deckInfo]);

  let cardData = props.deckInfo;
  let scale = 1; // props.mmWidth;
  let sW = cardData.cardInfo.w * (scale * 1);
  let sH = cardData.cardInfo.h * (scale * 1);
  let cards = [];

  let filteredCards = cardData.cards;
  if (props.cardFilters !== undefined) {
    const cleanedFilters = Object.fromEntries(
      props.cardFilters
        .filter((cardFilter) => cardFilter.key !== "")
        .map((cardFilter) => [cardFilter.key, cardFilter.value.split(",")]),
    );

    filteredCards = Object.fromEntries(
      Object.entries(filteredCards).filter(([_, cardParams]) =>
        Object.keys(cleanedFilters).every((param) =>
          cardParams[param].some((cardParamValue) => cleanedFilters[param].includes(cardParamValue)),
        ),
      ),
    );
  }

  for (let c of Object.keys(filteredCards)) {
    let card = filteredCards[c];
    if (card._exclude && card._exclude[0] === "x") {
      continue;
    }
    let count: number;
    if (card.__count === undefined || card.__count.length === 0 || props.onlyOneCard) {
      count = 1;
    } else {
      count = Number(card.__count[0]);
    }
    const pixelPerMm = 5.6705;
    for (let i = 0; i < count; ++i) {
      cards.push(
        <div
          className="card-print-wrap"
          style={{
            width: sW,
            height: sH,
            zoom: (props.mmWidth / cardData.cardInfo.w) * pixelPerMm,
          }}
        >
          <CgoCardSide
            w={cardData.cardInfo.w}
            h={cardData.cardInfo.h}
            printing={true}
            cardId={c}
            deckInfo={props.deckInfo}
            globalAssets={props.globalAssets}
            isRev={props.isRev}
            key={"card-" + c}
            projectFonts={props.projectFonts}
            deckFontOverrides={props.deckFontOverrides}
            cardFontOverrides={props.cardFontOverrides?.filter((font) => font.cardName === c)}
          />
        </div>,
      );
    }
  }
  return (
    <div className={"card-container printable"} style={{}}>
      {cards}
    </div>
  );
}

interface PrintWindowProps {
  projectSlug: string;
  deckName: string;
  deckInfo: DeckInfo;
  printRev: boolean;
  closeWindow: () => void;
  oneCardPerSheet: boolean;
  onlyOneCard: boolean;
  printingWidth: number;
  cardFilters?: Array<CardFilter>;
  globalAssets: { [key: string]: any };
  projectFonts: Array<ProjectFontDefinition>;
  deckFonts?: Array<FontStyleOverride>;
  cardFonts?: Array<FontStyleOverride>;
}

function PrintWindow(props: PrintWindowProps) {
  return (
    <NewWindow
      title={props.projectSlug + props.deckName + (props.printRev ? "-rev" : "")}
      onUnload={props.closeWindow}
      onOpen={async function (w) {
        loadFonts(w, props.deckInfo);
        setTimeout(() => {
          if (props.oneCardPerSheet) {
            const cardW_mm = props.deckInfo.cardInfo.w;
            const cardH_mm = props.deckInfo.cardInfo.h;

            const styleSheet = w.document.styleSheets[0];
            const mmWidth = props.printingWidth;
            const scaleFactor = mmWidth / cardW_mm;
            const mmHeight = cardH_mm * scaleFactor;

            styleSheet.insertRule(`
              @page {
                size: ${mmWidth}mm ${mmHeight}mm;
                margin: 0;
              }
            `);
          }
          // omit second arg, just wait for event loop to clear
        });
        setTimeout(() => {
          w.print();
        }, 1000);
      }}
    >
      <CardsContainer
        deckInfo={props.deckInfo}
        globalAssets={props.globalAssets}
        isRev={props.printRev}
        mmWidth={props.printingWidth}
        onlyOneCard={props.onlyOneCard}
        cardFilters={props.cardFilters}
        projectFonts={props.projectFonts}
        deckFontOverrides={props.deckFonts}
        cardFontOverrides={props.cardFonts}
      />
    </NewWindow>
  );
}

interface CardFilter {
  id: number;
  key: string;
  value: string;
}

interface DeckPdfExportFormProps {
  deckInfo: DeckInfo;
  projectSlug: string;
  deckName: string;
  globalAssets: { [key: string]: any };
  projectFonts: Array<ProjectFontDefinition>;
  deckFonts?: Array<FontStyleOverride>;
  cardFonts?: Array<FontStyleOverride>;
}

function DeckPdfExportForm(props: DeckPdfExportFormProps) {
  const [oneCardPerSheet, setOneCardPerSheet] = useState(true);
  const [printRev, setPrintRev] = useState(false);
  const [onlyOneCard, setOnlyOneCard] = useState(false);
  const [printingWidth, setPrintingWidth] = useState(Math.round((props.deckInfo.cardInfo?.w * 0.08467) / 2) * 2);
  const [isCardFilterEnabled, setIsCardFilterEnabled] = useState(false);
  const [cardFilters, setCardFilters] = useState<Array<CardFilter>>([{ id: 0, key: "", value: "" }]);
  const [isPrintWindowOpen, setIsPrintWindowOpen] = useState(false);
  const cardFilterIdRef = useRef(1);

  function printDeck(evt: FormEvent<HTMLFormElement>) {
    evt.preventDefault();
    setIsPrintWindowOpen(true);
  }

  return (
    <>
      <form onSubmit={printDeck}>
        <div>
          <label htmlFor="onePerSheet">One card per sheet</label>
          <input
            type="checkbox"
            name="onePerSheet"
            id="onePerSheet"
            checked={oneCardPerSheet}
            onChange={(evt) => setOneCardPerSheet(evt.target.checked)}
          ></input>
        </div>
        <div>
          <label htmlFor="cardWidth">Card width (mm)</label>
          <input
            type="number"
            step={0.1}
            value={printingWidth}
            name="cardWidth"
            id="cardWidth"
            onChange={(evt) => setPrintingWidth(Number(evt.target.value))}
          ></input>
        </div>
        <div>
          <label htmlFor={"saveRev"}>Save rev</label>
          <input
            type="checkbox"
            checked={printRev}
            name={"saveRev"}
            id={"saveRev"}
            onChange={(evt) => setPrintRev(evt.target.checked)}
          />
        </div>
        <div>
          <label htmlFor={"onlyOneCard"}>Every card only once</label>
          <input
            type="checkbox"
            checked={onlyOneCard}
            name={"onlyOneCard"}
            id={"onlyOneCard"}
            onChange={(evt) => setOnlyOneCard(evt.target.checked)}
          />
        </div>
        <div>
          <label htmlFor={"filterCards"}>Filter cards</label>
          <input
            type="checkbox"
            checked={isCardFilterEnabled}
            name={"filterCards"}
            id={"filterCards"}
            onChange={(evt) => setIsCardFilterEnabled(evt.target.checked)}
          />
        </div>
        {isCardFilterEnabled && (
          <div style={{ marginLeft: "1rem" }}>
            {cardFilters.map((cardFilter) => (
              <div key={cardFilter.id} style={{ display: "flex", gap: "1rem" }}>
                <select
                  value={cardFilter.key}
                  onChange={(evt) =>
                    setCardFilters((prev) =>
                      prev.map((item) => (item.id === cardFilter.id ? { ...item, key: evt.target.value } : item)),
                    )
                  }
                >
                  <option value="" disabled hidden>
                    Card parameter
                  </option>
                  {props.deckInfo.cards &&
                    Object.keys(Object.values(props.deckInfo.cards)[0]).map((param) => (
                      <option key={param} value={param}>
                        {param}
                      </option>
                    ))}
                </select>
                <span>=</span>
                <input
                  type="text"
                  placeholder={"Comma-separated list of values"}
                  size={30}
                  value={cardFilter.value}
                  onChange={(evt) =>
                    setCardFilters((prev) =>
                      prev.map((item) => (item.id === cardFilter.id ? { ...item, value: evt.target.value } : item)),
                    )
                  }
                />
                <button
                  type={"button"}
                  onClick={() => {
                    setCardFilters((prev) => prev.filter((item) => item.id !== cardFilter.id));
                  }}
                >
                  remove
                </button>
              </div>
            ))}

            <button
              type={"button"}
              onClick={() => {
                setCardFilters((prev) => [...prev, { id: cardFilterIdRef.current++, key: "", value: "" }]);
              }}
            >
              Add filter
            </button>
          </div>
        )}
        <input type="submit" value="Save as PDF"></input>
      </form>
      {isPrintWindowOpen && (
        <PrintWindow
          projectSlug={props.projectSlug}
          deckName={props.deckName}
          printRev={printRev}
          closeWindow={() => setIsPrintWindowOpen(false)}
          deckInfo={props.deckInfo}
          oneCardPerSheet={oneCardPerSheet}
          onlyOneCard={onlyOneCard}
          printingWidth={printingWidth}
          cardFilters={isCardFilterEnabled ? cardFilters : undefined}
          globalAssets={props.globalAssets}
          projectFonts={props.projectFonts}
          deckFonts={props.deckFonts}
          cardFonts={props.cardFonts}
        />
      )}
    </>
  );
}

export default DeckPdfExportForm;
